import { supabase } from "@/lib/supabase";
import type { Invoice, InvoiceItem, InvoiceListItem, Payment } from "@/types";

export interface SaleItemPayload {
  item_type: "product" | "service";
  product_id: string | null;
  service_id: string | null;
  name: string;
  quantity: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  cost_price: number;
  unit?: string | null;
}

export interface PaymentPayload {
  method: string;
  amount: number;
  reference?: string | null;
}

export interface CreateSaleParams {
  branchId: string;
  customerId: string | null;
  vehicleId: string | null;
  notes?: string | null;
  items: SaleItemPayload[];
  payments: PaymentPayload[];
}

export interface CreateSaleResult {
  ok: boolean;
  error: string | null;
  saleId?: string;
  invoiceId?: string;
  number?: string;
  ncf?: string | null;
  total?: number;
}

export async function createSale(params: CreateSaleParams): Promise<CreateSaleResult> {
  try {
    const { data, error } = await supabase.rpc("rkc_create_sale", {
      payload: {
        branch_id: params.branchId,
        customer_id: params.customerId ?? "",
        vehicle_id: params.vehicleId ?? "",
        notes: params.notes ?? "",
        items: params.items,
        payments: params.payments,
      },
    });

    if (error) {
      return { ok: false, error: error.message || "No se pudo registrar la venta." };
    }
    const result = data as {
      sale_id?: string;
      invoice_id?: string;
      number?: string;
      ncf?: string | null;
      total?: number;
    } | null;
    if (!result?.sale_id) {
      return { ok: false, error: "La venta no se pudo confirmar." };
    }
    return {
      ok: true,
      error: null,
      saleId: result.sale_id,
      invoiceId: result.invoice_id,
      number: result.number,
      ncf: result.ncf ?? null,
      total: Number(result.total ?? 0),
    };
  } catch {
    return { ok: false, error: "Ocurrió un error inesperado al cobrar." };
  }
}

export async function voidInvoice(invoiceId: string, reason: string | null): Promise<{ ok: boolean; error: string | null }> {
  try {
    const { error } = await supabase.rpc("rkc_void_invoice", {
      p_invoice_id: invoiceId,
      p_reason: reason ?? "Anulación de factura",
    });
    if (error) return { ok: false, error: error.message || "No se pudo anular la factura." };
    return { ok: true, error: null };
  } catch {
    return { ok: false, error: "Ocurrió un error inesperado al anular." };
  }
}

export async function refundInvoice(
  invoiceId: string,
  reason: string | null,
): Promise<{ ok: boolean; error: string | null }> {
  try {
    const { error } = await supabase.rpc("rkc_refund_invoice", {
      p_invoice_id: invoiceId,
      p_reason: reason ?? "Reembolso de factura",
    });
    if (error) return { ok: false, error: error.message || "No se pudo reembolsar la factura." };
    return { ok: true, error: null };
  } catch {
    return { ok: false, error: "Ocurrió un error inesperado al reembolsar." };
  }
}

export interface InvoiceDetail {
  invoice: Invoice;
  items: InvoiceItem[];
  payments: Payment[];
  saleId: string | null;
}

export async function fetchInvoiceDetail(invoiceId: string): Promise<InvoiceDetail | null> {
  try {
    const { data: invoice, error } = await supabase
      .from("invoices")
      .select("id, sale_id, branch_id, customer_id, number, ncf, invoice_type, subtotal, discount_total, tax_total, total, status, issued_by, created_at")
      .eq("id", invoiceId)
      .maybeSingle();
    if (error || !invoice) return null;

    const [itemsRes, paymentsRes] = await Promise.all([
      supabase
        .from("invoice_items")
        .select("id, invoice_id, description, quantity, unit_price, discount, tax_rate, line_total")
        .eq("invoice_id", invoiceId),
      invoice.sale_id
        ? supabase.from("payments").select("id, sale_id, method, amount, reference, created_at").eq("sale_id", invoice.sale_id)
        : Promise.resolve({ data: [] as Payment[], error: null }),
    ]);

    return {
      invoice: invoice as Invoice,
      items: (itemsRes.data ?? []) as InvoiceItem[],
      payments: (paymentsRes.data ?? []) as Payment[],
      saleId: invoice.sale_id ?? null,
    };
  } catch {
    return null;
  }
}

const INVOICE_COLUMNS =
  "id, sale_id, branch_id, customer_id, number, ncf, invoice_type, subtotal, discount_total, tax_total, total, status, issued_by, created_at";

export interface InvoicesData {
  invoices: InvoiceListItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export async function loadInvoices(): Promise<{ invoices: InvoiceListItem[]; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from("invoices")
      .select(INVOICE_COLUMNS)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) return { invoices: [], error: "No se pudieron cargar las facturas." };

    const rows = (data ?? []) as Invoice[];
    const customerIds = Array.from(new Set(rows.map((r) => r.customer_id).filter(Boolean))) as string[];
    const userIds = Array.from(new Set(rows.map((r) => r.issued_by).filter(Boolean))) as string[];
    const branchIds = Array.from(new Set(rows.map((r) => r.branch_id).filter(Boolean))) as string[];
    const saleIds = Array.from(new Set(rows.map((r) => r.sale_id).filter(Boolean))) as string[];

    const [customersRes, profilesRes, branchesRes, paymentsRes] = await Promise.all([
      customerIds.length
        ? supabase.from("customers").select("id, first_name, last_name").in("id", customerIds)
        : Promise.resolve({ data: [], error: null }),
      userIds.length
        ? supabase.from("profiles").select("id, full_name, last_name").in("id", userIds)
        : Promise.resolve({ data: [], error: null }),
      branchIds.length
        ? supabase.from("branches").select("id, name").in("id", branchIds)
        : Promise.resolve({ data: [], error: null }),
      saleIds.length
        ? supabase.from("payments").select("sale_id, method").in("sale_id", saleIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const customerMap = new Map(
      (customersRes.data ?? []).map((c: { id: string; first_name: string; last_name: string }) => [
        c.id,
        `${c.first_name} ${c.last_name}`.trim(),
      ]),
    );
    const profileMap = new Map(
      (profilesRes.data ?? []).map((p: { id: string; full_name: string; last_name: string }) => [
        p.id,
        `${p.full_name} ${p.last_name}`.trim(),
      ]),
    );
    const branchMap = new Map((branchesRes.data ?? []).map((b: { id: string; name: string }) => [b.id, b.name]));
    const paymentMap = new Map<string, string>();
    (paymentsRes.data ?? []).forEach((p: { sale_id: string | null; method: string | null }) => {
      if (p.sale_id && !paymentMap.has(p.sale_id)) paymentMap.set(p.sale_id, p.method ?? "");
    });

    const invoices: InvoiceListItem[] = rows.map((row) => ({
      ...row,
      customer_name: row.customer_id ? customerMap.get(row.customer_id) ?? null : null,
      cashier_name: row.issued_by ? profileMap.get(row.issued_by) ?? null : null,
      branch_name: row.branch_id ? branchMap.get(row.branch_id) ?? null : null,
      payment_method: row.sale_id ? paymentMap.get(row.sale_id) ?? null : null,
    }));

    return { invoices, error: null };
  } catch {
    return { invoices: [], error: "No se pudieron cargar las facturas." };
  }
}